import React, { useState, useEffect, IframeHTMLAttributes, PropsWithChildren } from "react";
import { createPortal } from "react-dom";

type EmbedIframeWrapperProps = {
    /**
     * CSS styles to apply to the iframe's window on initialization.
     * Use this string to specify inline CSS styles for the iframe's container,
     * such as `width: 100%; height: 500px; border: none;`.
     * This styling is applied once when the iframe is first rendered.
     */
    defaultWindowStyle?: string;
    /**
     * Scripts or script sources to embed within the iframe's window on initialization.
     * Supports various formats for flexibility:
     * 
     * - `{ [scriptId: string]: string }`: An object where each key uniquely identifies a script, and the value is a URL.
     * - `[string]`: An array of script URLs, allowing multiple scripts.
     * - `string`: A single script URL.
     * 
     * These scripts are injected into the iframe's window only once during initialization, allowing customization of the iframe's content.
     */
    defaultEmbedScripts?: { [scriptId in string]: string } | [string] | string;
    /**
     * Callback function triggered after the iframe finishes loading, useful for performing actions once the embed is fully available.
     * This function receives the iframe DOM element as a parameter, enabling manipulation
     * of the iframe or handling post-load tasks such as resizing adjustments.
     * @param iframeElement - The iframe DOM element that has just completed loading.
     * @returns 
     */
    onEmbedLoad?: (iframeElement: HTMLIFrameElement) => void;
} & IframeHTMLAttributes<HTMLIFrameElement>;

/**
 * `EmbedIframeWrapper` is a component designed to separate the window context by embedding content within an iframe.
 * This separation isolates the embedded content from the parent document, providing a secure, sandboxed environment.
 * 
 * It is particularly useful in scenarios where you want to:
 * - Isolate global objects like `window.FB` (e.g., Facebook SDK): By embedding the SDK or other scripts within an iframe, 
 *   you prevent them from affecting the main window context, which can help avoid conflicts with other scripts or libraries.
 * 
 */
const EmbedIframeWrapper: React.FC<PropsWithChildren<EmbedIframeWrapperProps>> = ({ children, defaultWindowStyle, defaultEmbedScripts, onEmbedLoad, style, ...iframeProps }) => {

    //This ref needs to be as `state`, cause we need to rerender, after load
    const [iframeRef, setIframeRef] = useState<HTMLIFrameElement | null>(null);
    const iframeDocument = iframeRef?.contentWindow?.document;
    const iframeBody = iframeDocument?.body;

    const [contentHeight, setContentHeight] = useState<number>(0);

    useEffect(() => {
        if (!iframeRef || !iframeDocument || !iframeBody)
            return;

        const iframeInnerWrapper = iframeDocument.getElementById("wrapper");

        if (!iframeInnerWrapper)
            return;

        //Observer - fit iframe size to content
        const resizeObserver = new ResizeObserver((entries) => {
            const rects = entries?.[0]?.contentRect;
            if (!rects) return;

            setContentHeight(rects.height);
            onEmbedLoad?.(iframeRef);
        });
        resizeObserver.observe(iframeInnerWrapper);

        //Attach styling inside iframe
        if (!iframeDocument.getElementById("iframe-style")) {
            const iframeStyle = document.createElement("style");
            iframeStyle.textContent = `
                body{
                overflow: hidden;
                margin: 0;
                }
                ${defaultWindowStyle ?? ""}
            `;
            iframeStyle.setAttribute("id", "iframe-style");
            iframeDocument.head.appendChild(iframeStyle);
        }

        //Attach embed scripts
        if (defaultEmbedScripts) {
            let embedScripts: { [scriptId in string]: string } = {};
            if (typeof defaultEmbedScripts === 'string')
                embedScripts['embed-js'] = defaultEmbedScripts;
            else if (Array.isArray(defaultEmbedScripts))
                defaultEmbedScripts.forEach((scriptUrl, scriptKey) => embedScripts[`embed-${scriptKey}-js`] = scriptUrl)
            else if (typeof embedScripts === 'object')
                embedScripts = defaultEmbedScripts;

            for (const [scriptKey, scriptUrl] of Object.entries(embedScripts)) {
                if (iframeDocument.getElementById(scriptKey))
                    continue;

                const embedScript = document.createElement("script");
                embedScript.type = "text/javascript";
                embedScript.async = true;
                embedScript.setAttribute("src", scriptUrl);
                embedScript.setAttribute("id", scriptKey);

                iframeBody.appendChild(embedScript);
            }
        }

        return () => {
            resizeObserver.disconnect();
        }
    }, [iframeRef]);

    return (
        <iframe
            {...iframeProps}
            ref={setIframeRef}
            style={{
                height: `${contentHeight}px`,
                width: '100%',
                border: 'none',
                position: 'relative',
                ...style
            }}
        >
            {iframeBody && createPortal((
                <div id="wrapper">
                    {children}
                </div>
            ), iframeBody)}
        </iframe>
    );

};

export default EmbedIframeWrapper;